import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, FloppyDisk, PaperPlaneTilt, X, Eye, FileXls, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

// PREVIEW-ONLY page. No real API calls. Just dummy data so the user can
// approve the UX before we wire the real backend.

const DUMMY_CARDS = [
  { id: 'c1', last4: '2378', limit: 30000 },
  { id: 'c2', last4: '1973', limit: 25000 },
  { id: 'c3', last4: '8847', limit: 18000 },
];

const SEED_ROWS = [
  { id: 1, card_id: 'c1', consumer_id: '7500000093071660', amount: 1010, status: 'success', remarks: '' },
  { id: 2, card_id: 'c1', consumer_id: '7000000093074374', amount: 1010, status: 'pending', remarks: 'OTP wait' },
  { id: 3, card_id: 'c2', consumer_id: '', amount: 0, status: 'pending', remarks: '' },
];

function fmt(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN');
}

function statusColor(s) {
  if (s === 'success') return 'bg-green-100 text-green-800 border-green-300';
  if (s === 'pending') return 'bg-amber-100 text-amber-800 border-amber-300';
  if (s === 'failed') return 'bg-red-100 text-red-700 border-red-300';
  return 'bg-gray-100 text-gray-600 border-gray-300';
}

function rowFill(s) {
  if (s === 'success') return 'bg-green-50/60';
  if (s === 'pending') return 'bg-amber-50/60';
  if (s === 'failed') return 'bg-red-50/60';
  return '';
}

function SubStaffWorkspace() {
  const [rows, setRows] = useState(SEED_ROWS);
  const [draftSaved, setDraftSaved] = useState(false);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { id: Date.now(), card_id: '', consumer_id: '', amount: 0, status: 'pending', remarks: '' },
    ]);
    setDraftSaved(false);
  };
  const updateRow = (id, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    setDraftSaved(false);
  };
  const removeRow = (id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    setDraftSaved(false);
  };

  const totals = rows.reduce(
    (acc, r) => {
      acc.count += 1;
      acc.amount += Number(r.amount) || 0;
      if (r.status === 'success') acc.success += 1;
      if (r.status === 'pending') acc.pending += 1;
      return acc;
    },
    { count: 0, success: 0, pending: 0, amount: 0 }
  );

  return (
    <div className="space-y-3">
      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <div className="text-[10px] uppercase text-blue-700 font-bold">Total Rows</div>
          <div className="text-2xl font-bold">{totals.count}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded p-3">
          <div className="text-[10px] uppercase text-green-700 font-bold">Success</div>
          <div className="text-2xl font-bold">{totals.success}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded p-3">
          <div className="text-[10px] uppercase text-amber-700 font-bold">Pending</div>
          <div className="text-2xl font-bold">{totals.pending}</div>
        </div>
        <div className="bg-fuchsia-50 border border-fuchsia-200 rounded p-3">
          <div className="text-[10px] uppercase text-fuchsia-700 font-bold">Total Amount</div>
          <div className="text-2xl font-bold">{fmt(totals.amount)}</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap sticky top-0 z-10 bg-white border rounded p-2">
        <Button size="sm" onClick={addRow} className="bg-blue-600 hover:bg-blue-700 gap-1">
          <Plus size={14} /> Add Row
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setDraftSaved(true);
            toast.success('Draft saved (preview)');
          }}
          className="gap-1"
        >
          <FloppyDisk size={14} /> {draftSaved ? 'Draft Saved' : 'Save Draft'}
        </Button>
        <Button
          size="sm"
          onClick={() => toast.success('Submitted to Staff (preview)')}
          className="bg-green-600 hover:bg-green-700 gap-1 ml-auto"
        >
          <PaperPlaneTilt size={14} /> Submit to Staff
        </Button>
      </div>

      {/* Excel-like table */}
      <div className="overflow-x-auto bg-white border rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
            <tr>
              <th className="px-2 py-2 text-center w-10">#</th>
              <th className="px-2 py-2 text-left">Card</th>
              <th className="px-2 py-2 text-left">Consumer ID</th>
              <th className="px-2 py-2 text-right">Amount</th>
              <th className="px-2 py-2 text-left">Status</th>
              <th className="px-2 py-2 text-left">Remarks</th>
              <th className="px-2 py-2 text-center w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.id} className={`border-t ${rowFill(r.status)}`}>
                <td className="px-2 py-1.5 text-center text-gray-500">{idx + 1}</td>
                <td className="px-2 py-1.5">
                  <select
                    value={r.card_id}
                    onChange={(e) => updateRow(r.id, 'card_id', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-xs"
                  >
                    <option value="">— Select —</option>
                    {DUMMY_CARDS.map((c) => (
                      <option key={c.id} value={c.id}>
                        ****{c.last4} · limit {fmt(c.limit)}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1.5">
                  <Input
                    value={r.consumer_id}
                    onChange={(e) => updateRow(r.id, 'consumer_id', e.target.value)}
                    className="h-8 text-xs font-mono"
                    placeholder="17-digit consumer ID"
                  />
                </td>
                <td className="px-2 py-1.5 text-right">
                  <Input
                    type="number"
                    value={r.amount || ''}
                    onChange={(e) => updateRow(r.id, 'amount', Number(e.target.value) || 0)}
                    className="h-8 text-xs text-right w-24"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <select
                    value={r.status}
                    onChange={(e) => updateRow(r.id, 'status', e.target.value)}
                    className={`px-2 py-1 border rounded text-xs font-medium ${statusColor(r.status)}`}
                  >
                    <option value="pending">Pending</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                  </select>
                </td>
                <td className="px-2 py-1.5">
                  <Input
                    value={r.remarks}
                    onChange={(e) => updateRow(r.id, 'remarks', e.target.value)}
                    className="h-8 text-xs"
                    placeholder="Optional"
                  />
                </td>
                <td className="px-2 py-1.5 text-center">
                  <button
                    onClick={() => removeRow(r.id)}
                    className="text-red-500 hover:text-red-700"
                    title="Remove row"
                  >
                    <X size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-gray-400 py-8 text-sm">
                  Koi row nahi — "+ Add Row" se start karo
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-gray-500">
        Color hint: Success → 🟢 green tinge, Pending → 🟡 amber, Failed → 🔴 red. Status dropdown se row auto-color hota hai.
      </div>
    </div>
  );
}

const TEAM_BREAKUP = [
  { name: 'Manoj', total: 150, success: 140, pending: 10, amount: 151000 },
  { name: 'Asit', total: 120, success: 115, pending: 5, amount: 120800 },
  { name: 'Rajendra', total: 180, success: 175, pending: 5, amount: 178200 },
  { name: 'Jitendra', total: 200, success: 190, pending: 10, amount: 198400 },
];

function StaffCombinedReport() {
  const grand = TEAM_BREAKUP.reduce(
    (acc, r) => {
      acc.total += r.total;
      acc.success += r.success;
      acc.pending += r.pending;
      acc.amount += r.amount;
      return acc;
    },
    { total: 0, success: 0, pending: 0, amount: 0 }
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-indigo-50 border border-indigo-200 rounded p-3">
          <div className="text-[10px] uppercase text-indigo-700 font-bold">Team Bookings</div>
          <div className="text-2xl font-bold">{grand.total}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded p-3">
          <div className="text-[10px] uppercase text-green-700 font-bold">Success</div>
          <div className="text-2xl font-bold">{grand.success}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded p-3">
          <div className="text-[10px] uppercase text-amber-700 font-bold">Pending</div>
          <div className="text-2xl font-bold">{grand.pending}</div>
        </div>
        <div className="bg-fuchsia-50 border border-fuchsia-200 rounded p-3">
          <div className="text-[10px] uppercase text-fuchsia-700 font-bold">Total Amount</div>
          <div className="text-2xl font-bold">{fmt(grand.amount)}</div>
        </div>
      </div>

      <div className="overflow-x-auto bg-white border rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Sub-Staff</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Success 🟢</th>
              <th className="px-3 py-2 text-right">Pending 🟡</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {TEAM_BREAKUP.map((r) => (
              <tr key={r.name} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 text-right font-semibold">{r.total}</td>
                <td className="px-3 py-2 text-right text-green-700">{r.success}</td>
                <td className="px-3 py-2 text-right text-amber-700">{r.pending}</td>
                <td className="px-3 py-2 text-right">{fmt(r.amount)}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => toast.info(`Drill-down: ${r.name} (preview)`)}>
                    <Eye size={12} /> View
                  </Button>
                </td>
              </tr>
            ))}
            <tr className="border-t bg-gray-50 font-bold">
              <td className="px-3 py-2">GRAND TOTAL</td>
              <td className="px-3 py-2 text-right">{grand.total}</td>
              <td className="px-3 py-2 text-right text-green-700">{grand.success}</td>
              <td className="px-3 py-2 text-right text-amber-700">{grand.pending}</td>
              <td className="px-3 py-2 text-right">{fmt(grand.amount)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => toast.success('Excel exported (preview)')} className="bg-emerald-600 hover:bg-emerald-700 gap-1">
          <FileXls size={16} /> Export Combined Excel
        </Button>
        <Button onClick={() => toast.success('Submitted to Admin (preview)')} className="bg-blue-600 hover:bg-blue-700 gap-1 ml-auto">
          <ArrowRight size={16} /> Submit All to Admin
        </Button>
      </div>
    </div>
  );
}

export default function Phase2PreviewPage() {
  const [view, setView] = useState('substaff');

  return (
    <div className="space-y-5" data-testid="phase2-preview-page">
      <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-900">
        <div className="font-bold">⚠️ PREVIEW MOCKUP — Live data nahi hai</div>
        Dono views same page pe stack ke dikha rahe hain. Approve karo toh real backend wire karta hoon (sub_bookings collection, draft/submit/approve flow, Excel export).
      </div>

      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-bold">Phase 2 Preview</h1>
        <div className="ml-auto flex gap-1 bg-gray-100 rounded p-1">
          <button
            onClick={() => setView('substaff')}
            className={`px-3 py-1 text-xs font-semibold rounded ${view === 'substaff' ? 'bg-white shadow' : 'text-gray-500'}`}
          >
            Sub-Staff View (Workspace)
          </button>
          <button
            onClick={() => setView('staff')}
            className={`px-3 py-1 text-xs font-semibold rounded ${view === 'staff' ? 'bg-white shadow' : 'text-gray-500'}`}
          >
            Staff View (Combined Report)
          </button>
        </div>
      </div>

      {view === 'substaff' && (
        <div>
          <div className="text-sm text-gray-600 mb-2">
            <b>Role:</b> Sub-Staff (e.g., Jitendra). Excel-like row-by-row booking. Card dropdown = sirf assigned cards. Save draft anytime, submit when ready.
          </div>
          <SubStaffWorkspace />
        </div>
      )}
      {view === 'staff' && (
        <div>
          <div className="text-sm text-gray-600 mb-2">
            <b>Role:</b> Team-Lead Staff (e.g., Ashish). Apni team ke har sub-staff ka rolled-up summary, drill-down, Excel export, final submit to admin.
          </div>
          <StaffCombinedReport />
        </div>
      )}
    </div>
  );
}
